import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    toggleSelectApp,
    selectAllApps,
    clearSelection,
    bulkToggleMinScale
} from '../../store/slices/monitoringSlice';
import { RootState, AppDispatch } from '../../store';
import StatusBadge from './StatusBadge';
import MinScaleToggleButton from './MinScaleToggleButton';
import { ContainerApp } from '../../types/monitoring';

interface ContainerAppsListProps {
    containerApps: ContainerApp[];
}

const ContainerAppsList: React.FC<ContainerAppsListProps> = ({ containerApps }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { selected, bulkActionLoading } = useSelector((state: RootState) => state.monitoring);
    const [selectMode, setSelectMode] = useState(false);


    // Handler for row click to navigate to details page
    const handleRowClick = (resourceGroup: string, id: string, isCheckbox: boolean) => {
        // Don't navigate if in select mode or if clicking on a checkbox
        if (selectMode || isCheckbox) return;

        const appName = id.split('/').pop();
        navigate(`/monitoring/apps/${resourceGroup}/${appName}`);
    };

    // Handler for checkbox click
    const handleCheckboxClick = (id: string) => {
        dispatch(toggleSelectApp(id));
    };

    // Toggle select mode
    const toggleSelectMode = () => {
        setSelectMode(!selectMode);
        if (selectMode) {
            // Clear selection when turning off select mode
            dispatch(clearSelection());
        }
    };

    // Bulk action handlers
    const handleSelectAll = () => {
        dispatch(selectAllApps());
    };

    const handleClearSelection = () => {
        dispatch(clearSelection());
    };

    const handleBulkToggleActive = (targetState: boolean) => {
        // Get the selected apps with their resource group and name
        const selectedApps = containerApps
            .filter(app => selected.includes(app.id))
            .map(app => ({
                resourceGroup: app.resourceGroup,
                appName: app.name.split('/').pop() || app.name,
                id: app.id
            }));
       
        if (selectedApps.length > 0) {
            dispatch(bulkToggleMinScale({ apps: selectedApps, targetState }));
        }
    };

    if (containerApps.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center text-gray-600 dark:text-gray-400">
                No container apps found.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Bulk Actions */}
            <div className="flex justify-between items-center">
                <button
                    onClick={toggleSelectMode}
                    className={`${selectMode ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white'} px-3 py-2 rounded-md transition-colors duration-200`}
                >
                    {selectMode ? 'Exit Selection Mode' : 'Select Apps'}
                </button>

                {selectMode && (
                    <div className="flex space-x-2">
                        <button
                            onClick={handleSelectAll}
                            className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white px-3 py-2 rounded-md"
                        >
                            Select All
                        </button>
                        <button
                            onClick={handleClearSelection}
                            className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white px-3 py-2 rounded-md"
                            disabled={selected.length === 0}
                        >
                            Clear Selection
                        </button>
                        <button
                            onClick={() => handleBulkToggleActive(true)}
                            className="bg-green-500 text-white px-3 py-2 rounded-md disabled:opacity-50"
                            disabled={selected.length === 0 || bulkActionLoading}
                        >
                            Set Always Active
                        </button>
                        <button
                            onClick={() => handleBulkToggleActive(false)}
                            className="bg-gray-500 text-white px-3 py-2 rounded-md disabled:opacity-50"
                            disabled={selected.length === 0 || bulkActionLoading}
                        >
                            Allow Scaling to Zero
                        </button>
                    </div>
                )}
            </div>

            {/* Container Apps Table */}
            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            {selectMode && (
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Select
                                </th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Resource Group</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Replicas</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Updated</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Always Active</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {containerApps.map((app) => (
                            <tr
                                key={app.id}
                                onClick={(e) => handleRowClick(app.resourceGroup, app.id, (e.target as HTMLElement).tagName === 'INPUT')}
                                className={`${selectMode ? '' : 'hover:bg-gray-50 dark:hover:bg-gray-700'} ${selected.includes(app.id) ? 'bg-blue-50 dark:bg-blue-900/30' : ''} ${selectMode ? 'cursor-default' : 'cursor-pointer'}`}
                            >
                                {selectMode && (
                                    <td className="px-3 py-4 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(app.id)}
                                            onChange={() => handleCheckboxClick(app.id)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                    </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{app.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <StatusBadge status={app.status} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500 dark:text-gray-400">{app.resourceGroup}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {app.replicas}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {new Date(app.lastUpdated).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <MinScaleToggleButton
                                        resourceGroup={app.resourceGroup}
                                        appName={app.name}
                                        id={app.id}
                                        isAlwaysActive={app.isAlwaysActive}
                                        minReplicas={app.minReplicas}
                                        disabled={bulkActionLoading}
                                        iconOnly={false}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Selected items counter */}
            {selectMode && selected.length > 0 && (
                <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg">
                    {selected.length} app{selected.length !== 1 ? 's' : ''} selected
                </div>
            )}
        </div>
    );
};

export default ContainerAppsList;